import jsQR from "jsqr";
import Webcam from "react-webcam";
import { AppContext } from '../../services/AppContext';
import React, { useContext, useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';

const QRScanner = () => {
    const { t } = useTranslation();
    const { apiClient, isAuthorized, user, notify } = useContext(AppContext);
    const [showModal, setShowModal] = useState(false);
    const [userNotes, setUserNotes] = useState("");
    const webcamRef = useRef(null);
    const [scanning, setScanning] = useState(true);
    const [selectedManager, setSelectedManager] = useState();
    const [managersData, setManagersData] = useState();

    const fetchData = async () => {
        if (user.role !== 'Admin') {
            return;
        }

        const now = new Date();

        const month = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0)).toISOString();
        const until = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0)).toISOString();

        const data = await apiClient.getManagers({
            from: month,
            to: until
        });

        if (!data.hasErrors) {
            setManagersData(data);
        }
    };

    const onScan = async (qrData, manager) => {
        try {
            const parts = qrData.split('/qr?id=');
            const qrId = parts[parts.length - 1];
            const result = await apiClient.getQR(qrId);

            const subscriptionParts = result.redirectUrl.split('/spend-class?id=');
            const subscriptionId = subscriptionParts[subscriptionParts.length - 1];

            const spentClassResult = await apiClient.spendClass({ subscriptionId, managerId: manager });

            if (!spentClassResult.hasErrors) {
                let message = t('pages.qr.notifications.successRegistered', { remaining: spentClassResult.after });

                if (spentClassResult.after === 0) {
                    message = t('pages.qr.notifications.subscriptionOver');
                }

                notify(message, "success");

                if (spentClassResult.userNotes) {
                    setUserNotes(spentClassResult.userNotes);
                    setShowModal(true);

                    return;
                }
            }
        } catch (error) {
            console.error("QR Scan Error:", error);
            notify(t('pages.qr.notifications.scanError'), "error");
        } finally {
        }

        setTimeout(() => setScanning(true), 500);
    };

    const handleClose = () => {
        setShowModal(false);
        setScanning(true);
    };

    useEffect(() => {
        if (managersData && managersData.length > 0) {
            setSelectedManager(managersData[0].id);
        }
    }, [managersData]);

    const handleManagerChange = (e) => {
        setSelectedManager(e.target.value);
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!isAuthorized || user.role === "Customer") return;

        const interval = setInterval(() => {
            if (webcamRef.current && scanning) {
                const video = webcamRef.current.video;
                if (video.readyState === video.HAVE_ENOUGH_DATA) {
                    const canvas = document.createElement("canvas");
                    const scanSize = 200; // Define scan box size
                    canvas.width = scanSize;
                    canvas.height = scanSize;
                    const ctx = canvas.getContext("2d");

                    const x = (video.videoWidth - scanSize) / 2;
                    const y = (video.videoHeight - scanSize) / 2;
                    ctx.drawImage(video, x, y, scanSize, scanSize, 0, 0, scanSize, scanSize);

                    const imageData = ctx.getImageData(0, 0, scanSize, scanSize);
                    const code = jsQR(imageData.data, imageData.width, imageData.height);

                    if (code) {
                        setScanning(false);
                        onScan(code.data, selectedManager);
                    }
                }
            }
        }, 250);

        return () => clearInterval(interval);
    }, [scanning, selectedManager]);

    return (
        <div>
            {managersData && <div>
                <label htmlFor="manager">{t('pages.qr.labels.scanningAs')}</label>
                <select
                    id="manager"
                    value={selectedManager}
                    onChange={handleManagerChange}>
                    {managersData.map(manager => {
                        return (
                            <option key={manager.id} value={manager.id}>
                                {manager.name}
                            </option>
                        );
                    })}
                </select>
            </div>}
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
                <div style={{ position: "relative", width: "400px", height: "400px" }}>
                    <Webcam
                        ref={webcamRef}
                        style={{ position: "absolute", width: "100%", height: "100%", objectFit: "cover" }}
                        videoConstraints={{
                            facingMode: "environment" // Prefer back camera, fallback to front if unavailable
                        }}
                    />

                    {/* Dark Overlay Outside Scan Box */}
                    <div style={{ position: "absolute", width: "100%", height: "100%" }}>
                        {/* Top overlay */}
                        <div
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "calc(50% - 75px)",
                                background: "rgba(0, 0, 0, 0.5)",
                            }}
                        ></div>

                        {/* Bottom overlay */}
                        <div
                            style={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                width: "100%",
                                height: "calc(50% - 75px)",
                                background: "rgba(0, 0, 0, 0.5)",
                            }}
                        ></div>

                        {/* Left overlay */}
                        <div
                            style={{
                                position: "absolute",
                                top: "calc(50% - 75px)",
                                left: 0,
                                width: "calc(50% - 75px)",
                                height: "150px",
                                background: "rgba(0, 0, 0, 0.5)",
                            }}
                        ></div>

                        {/* Right overlay */}
                        <div
                            style={{
                                position: "absolute",
                                top: "calc(50% - 75px)",
                                right: 0,
                                width: "calc(50% - 75px)",
                                height: "150px",
                                background: "rgba(0, 0, 0, 0.5)",
                            }}
                        ></div>
                    </div>

                    {/* Green Scanning Box */}
                    <div
                        style={{
                            position: "absolute",
                            width: "150px",
                            height: "150px",
                            border: "4px solid green",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            background: "transparent"
                        }}
                    ></div>
                </div>

                {/* Modal for user notes */}
                {
                    showModal && (
                        <div style={{
                            position: "fixed",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            backgroundColor: "white",
                            padding: "20px",
                            boxShadow: "0px 0px 10px rgba(0,0,0,0.3)"
                        }}>
                            <h3>{t('pages.qr.modal.userNotesTitle')}</h3>
                            <p>{userNotes}</p>
                            <button onClick={handleClose}>{t('pages.qr.modal.closeButton')}</button>
                        </div>
                    )
                }
            </div >
        </div>
    );
};

export default QRScanner;